import { useMemo } from 'react';

import { KeyedMutator } from 'swr';

import { StopTypes } from 'types/Appointment';
import { EmailTemplate, TemplateType } from 'types/EmailTemplates';
import { Maybe } from 'types/UtilityTypes';
import { useThrowableSWR } from 'utils/fetcher';

export type CarrierEmailTemplatesResponse = {
  dispatchTemplate: EmailTemplate;
  pickupTemplate: EmailTemplate;
  afterPickupTemplate: EmailTemplate;
  inTransitTemplate: EmailTemplate;
  dropoffTemplate: EmailTemplate;
  afterDropoffTemplate: EmailTemplate;
  podCollectionTemplate: EmailTemplate;
};

export type AppointmentEmailTemplatesResponse = {
  appointmentRequestTemplate: EmailTemplate;
  appointmentRequestTemplateHTML: EmailTemplate;
  apptRequestTemplates: EmailTemplate[];
};

export type CarrierQuoteEmailTemplatesResponse = {
  carrierQuoteEmailTemplates: EmailTemplate[];
};

// GET /email-templates lists every tier (user, group, service, generic) for one type.
export type EmailTemplatesListResponse = {
  templates: EmailTemplate[];
};

export type EmailTemplatesResponseMap = {
  [TemplateType.CARRIER]: CarrierEmailTemplatesResponse;
  [TemplateType.APPOINTMENT]: AppointmentEmailTemplatesResponse;
  [TemplateType.CARRIER_QUOTE_BY_GROUP]: CarrierQuoteEmailTemplatesResponse;
  [TemplateType.CARRIER_QUOTE_BY_LOCATION]: CarrierQuoteEmailTemplatesResponse;
  [TemplateType.QUICK_QUOTE_BATCH_LTL]: EmailTemplatesListResponse;
};

export type EmailTemplatesFetchResult<T extends TemplateType> = {
  emailTemplates: Maybe<EmailTemplatesResponseMap[T]>;
  invalidate: KeyedMutator<EmailTemplatesResponseMap[T]>;
  isLoading: boolean;
  error: Error;
};

export default function useFetchEmailTemplates<T extends TemplateType>(
  loadID?: Maybe<number>,
  templateType: T = TemplateType.CARRIER as T,
  stopType?: StopTypes,
  carrierQuoteQuery?: Maybe<string>
): EmailTemplatesFetchResult<T> {
  const endpoint = useMemo(() => {
    if (!loadID) return null;

    switch (templateType) {
      case TemplateType.CARRIER:
        return `load/${loadID}/sops/carrier`;
      case TemplateType.APPOINTMENT:
        return `appt/${loadID}/sops${stopType ? `?stopType=${stopType}` : ''}`;
      case TemplateType.CARRIER_QUOTE_BY_GROUP:
      case TemplateType.CARRIER_QUOTE_BY_LOCATION:
        return `email-templates/carrier-quote?${carrierQuoteQuery}&templateType=${templateType}`;
      default:
        return null;
    }
  }, [loadID, templateType, stopType, carrierQuoteQuery]);

  const { data, isLoading, mutate, error } =
    useThrowableSWR<EmailTemplatesResponseMap[T]>(endpoint);

  return {
    emailTemplates: data || null,
    invalidate: mutate,
    isLoading: isLoading,
    error: error,
  };
}
